'use client';

import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import { env } from '@/utils/env';
import { priceInfo as getPriceInfo } from '@/utils/price';
import React, { useCallback } from 'react';
import usePageTrack from '../hooks/usePageTrack';
import PCProductGroupItem from './PCProductGroupItem';
import { PgProduct, PgProductGroupItemProps } from './PgProductTypes';
import WapProductGroupItem from './WapProductGroupItem';

/**
 * PG专题页商品组展示组件 - 主组件（容器组件）
 * 根据设备类型渲染对应的子组件
 */
const PgProductGroupItem: React.FC<PgProductGroupItemProps> = React.memo(
  ({ productGroup, groupIndex, shouldLoadVideo = false }) => {
    const { trackProduct } = usePageTrack({ page_name: 'PG专题页A版' });
    const currency = useStore((state) => state.currency);
    const userInfo = useStore((state) => state.userInfo);

    // 统一的商品点击埋点处理
    const handleProductClick = useCallback(
      (product: PgProduct) => {
        try {
          const priceData = getPriceInfo(userInfo, product, currency);
          const basePriceData = getPriceInfo(userInfo, product, {
            name: env.NEXT_PUBLIC_DEFAULT_CURRENCY,
          });

          trackProduct('click', {
            module_name: '商品介绍',
            pits_index: groupIndex,
            product_sku: product.sku,
            business_sku: product.business_sku,
            shop_sku: product.shop_sku,
            product_id: `${product.id}`,
            product_name: product.short_name,
            product_price: parseFloat(priceData.price) || 0,
            current_product_price: parseFloat(priceData.sell_price) || 0,
            base_current_product_price: parseFloat(basePriceData.sell_price) || 0,
            dollar_exchange_rate: parseFloat(currency.exchange_rate) || 1,
            currency: currency.name,
          });
        } catch {
          // 埋点失败，忽略错误
        }
      },
      [trackProduct, groupIndex, userInfo, currency],
    );

    // 统一的商品曝光埋点处理
    const handleProductExposure = useCallback(
      (product: PgProduct) => {
        try {
          const priceData = getPriceInfo(userInfo, product, currency);
          const basePriceData = getPriceInfo(userInfo, product, {
            name: env.NEXT_PUBLIC_DEFAULT_CURRENCY,
          });

          trackProduct('exp', {
            module_name: '商品介绍',
            pits_index: groupIndex,
            product_sku: product.sku,
            business_sku: product.business_sku,
            shop_sku: product.shop_sku,
            product_id: `${product.id}`,
            product_name: product.short_name,
            product_price: parseFloat(priceData.price) || 0,
            current_product_price: parseFloat(priceData.sell_price) || 0,
            base_current_product_price: parseFloat(basePriceData.sell_price) || 0,
            dollar_exchange_rate: parseFloat(currency.exchange_rate) || 1,
            currency: currency.name,
          });
        } catch {
          // 埋点失败，忽略错误
        }
      },
      [trackProduct, groupIndex, userInfo, currency],
    );

    if (!productGroup || productGroup.length === 0) {
      return null;
    }

    return (
      <div className="product-group-item w-full mb-[50px] last:mb-0 md:mb-0">
        <WebOnly>
          <PCProductGroupItem
            productGroup={productGroup}
            groupIndex={groupIndex}
            onProductClick={handleProductClick}
            onProductExposure={handleProductExposure}
            shouldLoadVideo={shouldLoadVideo}
          />
        </WebOnly>
        <WapOnly>
          <WapProductGroupItem
            productGroup={productGroup}
            groupIndex={groupIndex}
            onProductClick={handleProductClick}
            onProductExposure={handleProductExposure}
            shouldLoadVideo={shouldLoadVideo}
          />
        </WapOnly>
      </div>
    );
  },
  // 自定义比较函数，防止不必要的重渲染
  (prevProps, nextProps) => {
    return (
      prevProps.groupIndex === nextProps.groupIndex &&
      prevProps.productGroup === nextProps.productGroup &&
      prevProps.shouldLoadVideo === nextProps.shouldLoadVideo
    );
  },
);

// 设置displayName便于调试
PgProductGroupItem.displayName = 'PgProductGroupItem';

export default PgProductGroupItem;
